<template>
<div class="container mt-5">
    <div v-if="loading" class="d-flex justify-content-center align-items-center position-fixed top-0 start-0 w-100 h-100 bg-white bg-opacity-75">
        <div class="spinner-border" role="status">
            <span class="visually-hidden">Loading...</span>
        </div>
    </div>

    <div :class="{ 'blurred-content': loading }">
        <h2 class="mb-4">{{ pageHeading }}</h2>
        <form>
            <!-- Row 1: Customer Name and Work Description -->
            <div class="row">
                <!-- Customer Name -->
                <div class="col-md-6 mb-3">
                    <label for="customername" class="form-label">Customer Name</label>
                    <select class="form-select" id="customername" v-model="quote.customername" :disabled="isEditing || isSubmitted" required>
                        <option value="">Select Customer</option>
                        <option v-for="customer in customers" :key="customer.id" :value="customer.displayName">
                            {{ customer.displayName }}
                        </option>
                    </select>
                </div>

                <!-- Work Description -->
                <div class="col-md-6 mb-3">
                    <label for="workdescription" class="form-label">Work Description</label>
                    <textarea class="form-control" id="workdescription" v-model="quote.workdescription" rows="3"></textarea>
                </div>
            </div>

            <!-- Row 2: Item, Quantity, Unit Price -->
            <div class="row">
                <!-- Item -->
                <div class="col-md-4 mb-3">
                    <label for="item" class="form-label">Item</label>
                    <select class="form-select" id="item" v-model="selectedItem.id" @change="updateUnitPrice" required :disabled="isSubmitted">
                        <option value="">Select Item</option>
                        <option v-for="item in items" :key="item.id" :value="item.id">
                            {{ item.displayName }}
                        </option>
                    </select>
                </div>

                <!-- Quantity -->
                <div class="col-md-4 mb-3">
                    <label for="quantity" class="form-label">Quantity</label>
                    <input type="number" class="form-control" id="quantity" v-model="selectedItem.quantity" min="1" required />
                </div>

                <!-- Unit Price -->
                <div class="col-md-4 mb-3">
                    <label for="unitPrice" class="form-label">Unit Price</label>
                    <input type="number" class="form-control" id="unitPrice" v-model="selectedItem.unitPrice" :readonly="true" />
                </div>
            </div>

            <!-- Add Item Button -->
            <div class="mb-3">
                <button type="button" class="btn btn-primary" @click="addItem" :disabled="isSubmitted">
                    Add Item

                </button>
            </div>

            <!-- Items Table -->
            <div v-if="quote.items.length" class="mb-4">
                <h4>Items</h4>
                <table class="table table-striped">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Image</th>
                            <th>Item Name</th>
                            <th>Quantity</th>
                            <th>Unit Price</th>
                            <th>Total Price</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(item, index) in quote.items" :key="index">
                            <td>{{ index + 1 }}</td>
                            <td>
                                <img :src="item.image" alt="Item Image" class="img-thumbnail" style="width: 40px; height: 40px; cursor: pointer" @click="viewImage(item.image)" />
                            </td>
                            <td>{{ item.displayName }}</td>
                            <td>{{ item.quantity }}</td>
                            <td>{{ item.unitPrice }}</td>
                            <td>{{ item.quantity * item.unitPrice }}</td>
                            <td>
                                <button class="btn btn-danger btn-sm" @click="removeItem(index)" aria-label="Delete quote" :disabled="isSubmitted">

                                    <!-- Font Awesome Trash Icon -->
                                    <i class="fas fa-trash-alt"></i>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <!-- Total Amount -->
                <div class="d-flex justify-content-end mt-3">
                    <h5>Total Amount: {{ totalAmount }}</h5>
                </div>
            </div>

            <!-- Submit Buttons -->
            <div>
                <button type="button" class="btn btn-primary" @click="saveQuote" :disabled="loading || isSubmitted">
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>

                    Save Quote
                </button>
                <button type="button" class="btn btn-secondary ms-2" @click="$router.back()">
                    Cancel
                </button>
                <button type="submit" class="btn btn-success ms-2" :disabled="loading || isSubmitted" @click="submitQuoteToBC">
                    Submit Quote to BC
                </button>
            </div>
        </form>

        <!-- Result Message Placeholder -->
        <div v-if="message" class="mt-4">
            <div :class="{
            'alert alert-success': isSuccess,
            'alert alert-danger': !isSuccess,
          }">
                {{ message }}
            </div>
        </div>

        <!-- Image Modal -->
        <div v-if="showModal" class="modal" tabindex="-1" role="dialog" style="display: block" @click.self="closeModal">
            <div class="modal-dialog modal-dialog-centered" role="document">
                <div class="modal-content">
                    <div class="modal-body text-center">
                        <img :src="modalImage" alt="Large Image" class="img-fluid" />
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="closeModal">
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
</template>

<script>
import {
    getQuoteById,
    getValueByKey
} from "@/helper/utils1"; // Use `@` for the src directory
import {
    getCustomers,
    getItems,
    getQuotesFromCareDB,
    saveQuoteToCareDB,
    submitQuoteToBC,
} from "@/helper/RestCalls";

export default {
    data() {
        return {
            quote: {
                id: "",
                customerid: "",
                customername: "",
                workdescription: "",
                salesperson: "",
                items: [], // Array to store selected items
            },
            editingQuote: {
                customername: "",
                items: [], // Array to store selected items
            },
            selectedItem: {
                id: "",
                displayName: "",
                quantity: 1,
                unitPrice: 0,
                image: null,
            },
            customers: [], // Populate this with actual customer data
            items: [], // Populate this with actual item data
            quotes: [],
            message: "",
            isSuccess: false,
            loading: false,
            showModal: false,
            modalImage: "",
            pageHeading: "Create Quote",
            isEditing: false,
            isSubmitted: false,
        };
    },
    async mounted() {
        this.loading = true;
        this.customers = await getCustomers();
        this.items = await getItems();
        this.checkForEditMode();
        this.loading = false;
    },
    computed: {
        totalAmount() {
            return this.quote.items.reduce(
                (total, item) => total + item.quantity * item.unitPrice,
                0
            );
        },
    },
    methods: {
        // Check if we are in Edit mode (query string param 'quoteid')
        async checkForEditMode() {
            console.log("checkForEditMode1");
            const quoteId = this.$route.query.quoteid;
            console.log("checkForEditMode2 :" + quoteId + ":");

            if (quoteId) {
                console.log("checkForEditMode3");
                this.isEditing = true;
                this.quotes = await getQuotesFromCareDB();
                console.log("checkForEditMode4");
                this.fetchQuoteData(quoteId);
                if(this.isSubmitted)
                {
                    this.pageHeading = "Submitted Quote";
                }
                else{
                    this.pageHeading = "Edit Quote";
                }
                console.log("checkForEditMode5");
            }
        },
        fetchQuoteData(quoteId) {
            // Example API call to fetch quote details by ID
            console.log | "fetchQuoteData>>";
            this.quote = getQuoteById(this.quotes, quoteId);
            this.isSubmitted = this.quote.submitted;
            console.log(this.quote);
            console.log | "<<fetchQuoteData";
        },

        viewImage(image) {
            this.modalImage = image;
            this.showModal = true;
        },
        closeModal() {
            this.showModal = false;
            this.modalImage = "";
        },

        updateUnitPrice() {
            // Find the selected item and update unit price
            const item = this.items.find((i) => i.id === this.selectedItem.id);
            if (item) {
                this.selectedItem.displayName = item.displayName;
                this.selectedItem.unitPrice = item.unitPrice;
                this.selectedItem.image = item.image;
            }
        },
        addItem() {
            // Ensure all fields are valid
            if (!this.selectedItem.id || !this.selectedItem.quantity) {
                alert("Please select an item and enter a valid quantity.");
                return;
            }

            // Add the selected item to the quote items array
            this.quote.customerid = getValueByKey(
                this.customers,
                "displayName",
                this.quote.customername,
                "id"
            );
            console.log("additem custid :" + this.quote.customerid);
            this.quote.items.push({
                ...this.selectedItem
            });

            // Reset selected item fields
            this.selectedItem = {
                id: "",
                displayName: "",
                quantity: 1,
                unitPrice: 0,
            };
        },
        removeItem(index) {
            // Remove an item from the quote items array
            this.quote.items.splice(index, 1);
        },
        async saveQuote() {
            // Logic to save the quote
            try {
                this.loading = true;
                this.message = "";
                let response = await saveQuoteToCareDB(this.quote);
                this.isSuccess = true;
                const {
                    message,
                    quoteid
                } = response.data; // Destructure the response data
                this.message = message;
                if (quoteid !== undefined && quoteid !== null && quoteid !== "") {
                    this.quote.id = quoteid;
                }
                this.isEditing = true;
            } catch (err) {
                this.isSuccess = false;
                console.error(err);
            } finally {
                this.loading = false;
            }
        },
        async submitQuoteToBC() {
            // Logic to submit the quote to BC
            try {
                this.loading = true;
                this.message = "";
                await submitQuoteToBC(this.quote);
                this.isSuccess = true;
                this.isEditing = true;
                this.isSubmitted = true;
                this.message = "Successfully submitted the quote to BC. This quote is now readonly here!";
            } catch (err) {
                this.isSuccess = false;
                this.message = "Failed to submit quote in BC!";
                console.error(err);
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.blurred-content {
    filter: blur(5px);
    /* Adjust the blur radius as needed */
    pointer-events: none;
    /* Disable interactions */
    transition: filter 0.3s ease;
    /* Optional: Smooth transition for blur effect */
}
</style>
